import styles from './VerifyDownloadFile.module.scss';
import { Document, Page, Image, StyleSheet, pdf } from '@react-pdf/renderer';
import Modal from '../Modal';
import FormHeader from '../../atoms/FormHeader';
import GenericForm from '../../molecules/GenericForm';
import Input from '../../atoms/Input';
import { Alert } from 'antd';
import Button from '../../atoms/Button';
import { FC, useState } from 'react';
import { setInfo } from '../../../store/slices/infoSlice';
import { raiseException } from '../../../utils/raiseException';
import { trpc } from '../../../utils/trpc';
import { useAppDispatch } from '../../../customHooks/reduxHooks';
import * as pdfjsLib from 'pdfjs-dist';
import { generateInformedConsentPdf } from '../GenerateInformedConsentPdf/GenerateInformedConsentPdf';
import { getNormalizedDate } from '../../../utils/dateTime';
import { useIsMobile } from '../../../customHooks/useIsMobile';
import { downloadFileUtils } from '../../../utils/downloadFileUtils';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@5.0.375/build/pdf.worker.min.mjs';

interface VerifyDownloadFileProps {
  verifyLogs: any;
  myTherapist: any;
  selectedDownloadFile: any;
  setVerifyLogs: any;
  handleDownloadDocument: any;
  patient?: any;
  informedConsent?: boolean;
  informedConsentResponse?: any
}

export const VerifyDownloadFile: FC<VerifyDownloadFileProps> = ({
  verifyLogs,
  myTherapist,
  selectedDownloadFile,
  setVerifyLogs,
  handleDownloadDocument,
  patient,
  informedConsent = false,
  informedConsentResponse
}) => {
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState<string>('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);

  const dispatch = useAppDispatch();
  const isMobile: any = useIsMobile();

  const verifyDocumentDownloadCode = trpc.mainService.medicalDocuments.verifyDocumentDownloadCode.useMutation();

  const downloadFile = async (url: string, filename: string) => {
    const extension = filename?.split('.').pop()?.toLowerCase();
    try {
      if (extension === 'png' || extension === 'jpeg' || extension === 'jpg') {
        await downloadImageWithWatermark(url, filename, `image/${extension}`);
      } else if (extension === 'pdf') {
        await downloadPdfWithWatermark(url, filename, 'application/pdf');
      } else {
        console.error('Unsupported file type');
      }
    } catch (e) {
      console.error('Error processing file:', e);
    }
  };

  const getImageDimensions = (base64: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve({width: img.width, height: img.height});
      img.onerror = reject;
      img.src = base64;
    });
  };


  const PdfWithWatermark = ({imageUrl, pageWidth, pageHeight}) => (
    <Document>
      <Page size={{width: pageWidth, height: pageHeight}} style={stylesPdf.page}>
        <Image src={imageUrl} style={stylesPdf.image}/>
      </Page>
    </Document>
  );

  const stylesPdf = StyleSheet.create({
    page: {margin: 0, padding: 0},
    image: {width: '100%', height: '100%'},
  });

  const addWatermarkToCanvas = (context: CanvasRenderingContext2D, width: number, height: number) => {
    const date = getNormalizedDate();
    const watermarkLine0 = `DOCUMENTO RISERVATO DI MYMENTIS - VIETATA LA RIPRODUZIONE`;
    const watermarkLine1 = `Scaricato da mymentis.it, in data ${date.toLocaleDateString('it-IT')} alle ${date.toLocaleString('IT', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
    const watermarkLine2 = `dal professionista ${myTherapist?.given_name} ${myTherapist?.family_name}`;
    const fontSize = Math.max(18, Math.min(42, width * 0.015));
    context.font = `${fontSize}px Poppins`;
    context.fillStyle = 'rgba(128, 128, 128, 0.8)';
    context.textAlign = 'center';
    context.fillText(watermarkLine0, width / 2, height - fontSize * 3);
    context.fillText(watermarkLine1, width / 2, height - fontSize * 2);
    context.fillText(watermarkLine2, width / 2, height - fontSize);
  };

  const createPdfFromCanvas = async (canvas: HTMLCanvasElement, width: number, height: number, filename: string, mimetype: string) => {
    const watermarkedImageUrl = canvas.toDataURL('image/png');
    const blobPdf = await pdf(<PdfWithWatermark imageUrl={watermarkedImageUrl} pageWidth={width}
                                                pageHeight={height}/>).toBlob();
    downloadBlob(blobPdf, filename, mimetype);
  };

  const downloadBlob = (blob: Blob, filename: string, mimetype: string) => {
    if (!isMobile) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      if ((window as any)?.AppActions?.saveFile) {
        downloadFileUtils(blob, filename, mimetype)
      }
    }
  };

  const downloadImageWithWatermark = async (imageUrl: string, filename: string, mimetype: string) => {
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error('Failed to fetch image');
    const blob = await response.blob();
    const base64data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const {width, height} = await getImageDimensions(base64data);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to get 2D context');
    const img = new window.Image();
    img.src = base64data;
    await new Promise((resolve) => (img.onload = resolve));
    context.drawImage(img, 0, 0, width, height);
    addWatermarkToCanvas(context, width, height);
    await createPdfFromCanvas(canvas, width, height, `${filename.split('.')[0]}.pdf`, mimetype);
  };

  const downloadPdfWithWatermark = async (pdfUrl: string, filename: string, mimetype: string) => {
    const response = await fetch(pdfUrl);
    if (!response.ok) throw new Error('Failed to fetch PDF');
    const pdfData = await response.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({data: pdfData});
    const pdfDocument = await loadingTask.promise;
    const page = await pdfDocument.getPage(1);
    const originalViewport = page.getViewport({scale: 1});
    const originalWidth = originalViewport.width;
    const originalHeight = originalViewport.height;
    const scale = 1.5;
    const renderViewport = page.getViewport({scale});
    const canvas = document.createElement('canvas');
    canvas.width = renderViewport.width;
    canvas.height = renderViewport.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to get 2D context');
    await page.render({
      canvasContext: context,
      viewport: renderViewport,
    }).promise;
    addWatermarkToCanvas(context, renderViewport.width, renderViewport.height);
    await createPdfFromCanvas(canvas, originalWidth, originalHeight, `${filename.split('.')[0]}_watermarked.pdf`, mimetype);
  };

  const handleVerifyDocumentDownloadCode = async () => {
    if (!confirmCode || !verifyLogs.id || !selectedDownloadFile.fileUrl) {
      return;
    }
    try {
      const response = await verifyDocumentDownloadCode.mutateAsync({
        logsId: verifyLogs.id,
        code: confirmCode
      });
      if (response.success) {
        if (!informedConsent) {
          await downloadWithoutWatermark();
        } else {
          await downloadWithWatermark();
        }
        setVerifyLogs(null);
        dispatch(
          setInfo({
            text: 'Il codice è stato applicato con successo e il download del file è iniziato.',
            variant: 'confirm',
          })
        );
      } else {
        setVerifyLogs(null);
        dispatch(
          setInfo({
            text: 'È stato inserito tre volte il codice sbagliato, si prega di riprovare.',
            variant: 'warn',
          })
        );
      }
    } catch (e: any) {
      handleVerificationError(e);
    }
  };

  const downloadWithoutWatermark = async () => {
    try {
      await downloadFile(selectedDownloadFile.fileUrl, selectedDownloadFile.filename);
      if (!selectedDownloadFile.fileUrl.includes('blob')) {
        URL.revokeObjectURL(selectedDownloadFile.fileUrl);
      }
    } catch (e) {
      console.error('Error downloading document:', e);
      raiseException(e);
      dispatch(
        setInfo({
          text: 'Qualcosa è andato storto durante il download del documento.',
          variant: 'error',
        })
      );
      throw e;
    }
  };

  const downloadWithWatermark = async () => {
    try {
      const blob = await generateInformedConsentPdf(myTherapist, patient, informedConsentResponse);
      const blobUrl = URL.createObjectURL(blob);
      await downloadPdfWithWatermark(blobUrl, selectedDownloadFile.filename, 'application/pdf');
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Error adding watermark to Informed Consent PDF:', e);
      throw e;
    }
  };

  const handleVerificationError = (e: any) => {
    if (e?.message === 'Wrong code') {
      setError('wrongCode');
    } else if (e?.message === 'Expired code') {
      setError('expiredCode');
    } else {
      setError('unknown');
    }
    raiseException(e);
    console.error('Verification error:', e);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setConfirmCode(value.slice(0, 6));
    setError('');
  };

  const resendCodeFunction = async () => {
    if (isResendDisabled) return;

    setError('');
    setConfirmCode('');
    await handleDownloadDocument();

    setIsResendDisabled(true);
    setResendTimer(60);

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };


  return (
    <Modal modal subClassName={styles.confirmCodeModal}>
      <FormHeader
        title="Inserire il codice"
        closeModal={() => {
          setVerifyLogs(null);
        }}
        closeButton
      />
      <GenericForm
        className={styles.confirmCodeForm}
        title=""
        description="Ti abbiamo inviato una mail. Per proseguire, inserisci il codice che trovi al suo interno:"
        answer={[
          <div className={styles.formInputs}>
            <Input
              className={styles.codeInput}
              value={confirmCode}
              onChange={handleInputChange}
              error={error === 'wrongCode' ? 'Il codice inserito non è corretto' : undefined}
              placeholder="Inserisci il codice"
              maxLength={6}
            />
            {error && error !== 'wrongCode' && (
              <Alert
                className={styles.alertError}
                type="error"
                message={
                  error === 'expiredCode' ? (
                    <div>
                      Il codice per scaricare il file è scaduto.{' '}
                      <span
                        className={styles.underlinedLink}
                        onClick={resendCodeFunction}
                      >
                        Clicca qui
                      </span>{' '}
                      per richiedere nuovamente un nuovo codice.
                    </div>
                  ) : (
                    'Si è verificato un errore inaspettato. Verifica di essere connesso a internet e riprova.'
                  )
                }
                closable
              />
            )}
          </div>,
        ]}
        cta={[
          <div>
            <Button onClick={handleVerifyDocumentDownloadCode} disabled={confirmCode.length !== 6}>
              Verifica e scarica il documento.
            </Button>
            <div className={styles.confirmCodeBtn}>
              Non hai ricevuto il codice?{' '}
              <span
                className={`${styles.spanBtn} ${isResendDisabled ? styles.disabled : ''}`}
                onClick={!isResendDisabled ? resendCodeFunction : undefined}
              >
              {isResendDisabled ? `Invia un altro codice (${resendTimer}s)` : 'Invia un altro codice'}
            </span>
              <div
                className={styles.attention}>{'Se non trovi la mail, ricordati di controllare anche nello spam'}</div>
            </div>
          </div>,
        ]}
      />
    </Modal>
  );
};
